import React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { serializeParams } from 'utils';
import useSearchQueryParams from 'hooks/use-search-params';
import useScheduleViewConfig from 'hooks/useScheduleViewConfig';
import useTabSwapAnchor from 'hooks/useTabSwapAnchor';
import Tabs from 'components/Tabs/Tabs';
import { ANALYTICS_EVENT, ANALYTICS_PROPERTY } from '@kouto/types';
import { analyticsManager } from 'features/analytics';

interface ScheduleViewTabsProps {
  readonly active: 'browse' | 'schedule';
}

/**
 * Browse | Schedule tab bar shared by the landing (Browse active) and the schedule view
 * (Schedule active). Navigates between the catalog (#!/e) and the schedule (#!/calendar),
 * preserving the current query params (e.g. fromDate, categoryIds).
 */
const ScheduleViewTabs = ({ active }: ScheduleViewTabsProps) => {
  const history = useHistory();
  const { t: translate } = useTranslation();
  const { searchParams } = useSearchQueryParams();
  const { showTabs } = useScheduleViewConfig();
  // A swap in either direction keeps the sticky tab bar where it was (see useTabSwapAnchor).
  const { captureSwapAnchor, tabsRef } = useTabSwapAnchor<HTMLDivElement>();

  if (!showTabs) return null;

  // Browse = the home landing (root). `/e` is the filtered results view, so we navigate to '/'.
  // Drop the schedule-only `fromDate` so the catalog isn't filtered to an empty state.
  const goBrowse = () => {
    const browseParams = { ...searchParams };
    delete browseParams.fromDate;
    captureSwapAnchor();
    history.push({ pathname: '/', search: serializeParams(browseParams) });
  };
  const goSchedule = () => {
    captureSwapAnchor();
    history.push({
      pathname: '/calendar',
      search: serializeParams(searchParams),
    });
  };

  const labels = [
    translate('browse', { defaultValue: 'Browse' }),
    translate('schedule', { defaultValue: 'Schedule' }),
  ];

  const handleTabClick = (index: number) => {
    const clickedTab = index === 0 ? 'browse' : 'schedule';
    if (clickedTab === active) return;
    analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_TAB, {
      [ANALYTICS_PROPERTY.Tab]: labels[index],
    });
    if (clickedTab === 'browse') goBrowse();
    else goSchedule();
  };

  return (
    <TabsRegion
      ref={tabsRef}
      aria-label={translate('scheduleViewSelector', {
        defaultValue: 'Schedule view selector',
      })}
      className="schedule-view__tabs"
      role="region"
    >
      <Tabs
        asBtn
        centered
        classNamePrefix="schedule-view__"
        labelAs="h3"
        onTabClick={handleTabClick}
        selectedIndex={active === 'browse' ? 0 : 1}
        tabsLabel={labels}
      />
    </TabsRegion>
  );
};

export default ScheduleViewTabs;

const TabsRegion = styled.div`
  .schedule-view__tab-label:focus-visible {
    outline: 2px solid var(--way-colors-focusBorderColor);
    border-radius: 4px;
  }
`;
